// iter-81al — Employee compliance profile modal.
// Opens for a single staff record. Shows:
//   - header with name / position / OPIN / hire date / overall rollup
//   - each checklist item with color-coded status pill, dates, and attached docs
//   - unassigned docs at the bottom (docs the AI couldn't tie to a requirement)
//   - actions: AI upload, edit, print (with certifications), records ZIP
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError, isVaultRelockError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2, CheckCircle2, AlertTriangle, Clock, XCircle, FileText, Download,
  Upload, Printer, Pencil, FolderArchive, Sparkles, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/dates";

const STATUS_MAP = {
  current:       { label: "Current",      Icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  expiring_soon: { label: "Expiring ≤30", Icon: Clock,        cls: "bg-amber-100 text-amber-900 border-amber-200" },
  expiring_60:   { label: "Expires ≤60",  Icon: Clock,        cls: "bg-amber-50 text-amber-800 border-amber-200" },
  expiring_90:   { label: "Expires ≤90",  Icon: Clock,        cls: "bg-stone-50 text-stone-700 border-stone-200" },
  expired:       { label: "Expired",      Icon: XCircle,      cls: "bg-red-100 text-red-800 border-red-200" },
  missing:       { label: "Missing",      Icon: AlertTriangle, cls: "bg-red-50 text-red-700 border-red-200" },
  review:        { label: "Review",       Icon: AlertTriangle, cls: "bg-amber-50 text-amber-800 border-amber-200" },
  verify:        { label: "Verify",       Icon: AlertTriangle, cls: "bg-sky-50 text-sky-800 border-sky-200" },
  na:            { label: "N/A",          Icon: FileText,     cls: "bg-stone-100 text-stone-500 border-stone-200" },
};

/** iter-81c2c — PD Hours Ledger widget. Reads the annual-hour rows out of
 * `checklist` and renders a rolling progress bar so directors can see at a
 * glance who's at 4/10, 8/10, etc. Green when at 100%, amber below.
 * Includes SUTQ Topic Areas #1 and #2 (1-hour floor each). */
function PDHoursLedger({ checklist }) {
  const hourRows = (checklist || []).filter(r => r.required_hours);
  if (!hourRows.length) return null;
  const totalRequired = hourRows.reduce((s, r) => s + (r.required_hours || 0), 0);
  const totalLogged = hourRows.reduce((s, r) => s + Number(r.state?.hours || 0), 0);
  const overallPct = Math.min(100, Math.round((totalLogged / totalRequired) * 100));
  const overallDone = totalLogged >= totalRequired;
  return (
    <div className="mt-4 rounded-xl border border-stone-200 bg-white overflow-hidden" data-testid="pd-hours-ledger">
      <div className="px-4 py-2 bg-gradient-to-r from-emerald-50 to-emerald-100/50 border-b border-emerald-200 flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest font-semibold text-emerald-800 flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Professional Development — Annual Ledger
        </div>
        <div className={`text-xs font-bold ${overallDone ? "text-emerald-700" : "text-amber-700"}`}>
          {totalLogged.toString().replace(/\.0$/, "")} of {totalRequired} hrs · {overallPct}%
        </div>
      </div>
      <div className="divide-y divide-stone-100">
        {hourRows.map(row => {
          const logged = Number(row.state?.hours || 0);
          const req = row.required_hours || 0;
          const pct = Math.min(100, Math.round((logged / req) * 100));
          const done = logged >= req;
          return (
            <div key={row.key} className="px-4 py-2.5" data-testid={`pd-row-${row.key}`}>
              <div className="flex items-center justify-between gap-3 mb-1.5">
                <div className="text-sm text-stone-800 font-medium truncate">{row.label}</div>
                <div className={`text-xs font-semibold shrink-0 ${done ? "text-emerald-700" : logged > 0 ? "text-amber-700" : "text-red-600"}`}>
                  {logged.toString().replace(/\.0$/, "")} / {req} hrs
                </div>
              </div>
              <div className="w-full h-2 rounded-full bg-stone-100 overflow-hidden">
                <div
                  className={`h-full transition-all rounded-full ${done ? "bg-emerald-500" : logged > 0 ? "bg-amber-500" : "bg-red-400"}`}
                  style={{ width: `${Math.max(4, pct)}%` }}
                  data-testid={`pd-bar-${row.key}`}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="px-4 py-1.5 bg-stone-50 text-[11px] text-stone-500 border-t border-stone-100">
        Hours auto-sum from OPR training certificates uploaded to this staff file. Edit any row above to add or adjust manually.
      </div>
    </div>
  );
}

function StatusChip({ code, label }) {
  const meta = STATUS_MAP[code] || STATUS_MAP.review;
  const Icon = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${meta.cls}`}>
      <Icon className="w-3 h-3" /> {label || meta.label}
    </span>
  );
}

export default function StaffFileProfile({
  open, onOpenChange, recordId, onEdit, onUpload, onDismiss,
}) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewer, setReviewer] = useState("");

  useEffect(() => {
    if (!open || !recordId) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/staff/records/${recordId}/profile`);
        setProfile(res.data);
      } catch (err) {
        if (!isVaultRelockError(err)) toast.error(formatApiError(err));
        onOpenChange(false);
      } finally { setLoading(false); }
    };
    load();
  }, [open, recordId, onOpenChange]);

  const refetch = async () => {
    if (!recordId) return;
    try {
      const res = await api.get(`/staff/records/${recordId}/profile`);
      setProfile(res.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const record = profile?.record || {};
  const rollup = profile?.rollup || {};
  const checklist = profile?.checklist || [];
  const unassigned = profile?.unassigned_docs || [];

  const sections = useMemo(() => {
    const g = {}; const order = [];
    for (const row of checklist) {
      const s = row.section || "Other";
      if (!g[s]) { g[s] = []; order.push(s); }
      g[s].push(row);
    }
    return { g, order };
  }, [checklist]);

  const downloadDoc = async (doc) => {
    try {
      const res = await api.get(`/staff/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.content_type || "application/octet-stream" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/staff/documents/${doc.id}`);
      toast.success("Document deleted");
      refetch();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const printChecklist = async (withCerts) => {
    try {
      const params = { t: Date.now() };
      if (reviewer.trim()) params.reviewer_name = reviewer.trim();
      if (withCerts) params.include_certifications = "1";
      const res = await api.get(`/staff/records/${recordId}/checklist.pdf`, {
        responseType: "blob", params,
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadZip = async () => {
    try {
      const res = await api.get(`/staff/records/${recordId}/records.zip`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(record.staff_name || "staff").replace(/\s+/g, "_")}-records.zip`;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const overallCode = rollup.expired_count > 0
    ? "expired"
    : rollup.missing_count > 0
      ? "missing"
      : rollup.expiring_soon_count > 0
        ? "expiring_soon"
        : "current";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="staff-profile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center font-semibold text-sm">
              {(record.staff_name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}
            </div>
            <div>
              <div className="text-lg font-semibold text-stone-900">{record.staff_name || "—"}</div>
              <div className="text-xs text-stone-500 font-normal">
                {record.position || "—"}
                {record.opin_number && <> · OPIN {record.opin_number}</>}
                {record.date_of_hire && <> · Hired {fmtDate(record.date_of_hire)}</>}
              </div>
            </div>
          </DialogTitle>
          <DialogDescription>
            Compliance profile · every checklist requirement with matched documents and status.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-stone-500 text-sm">
            <Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> Loading profile…
          </div>
        ) : (
          <>
            {/* Rollup + actions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
              <RollupTile code={overallCode} label="Overall" value={STATUS_MAP[overallCode]?.label || "—"} />
              <RollupTile code="expired" label="Expired" value={rollup.expired_count ?? 0} />
              <RollupTile code="expiring_soon" label="Expiring ≤30d" value={rollup.expiring_soon_count ?? 0} />
              <RollupTile code="missing" label="Missing" value={rollup.missing_count ?? 0} />
            </div>

            <div className="mt-4 flex flex-wrap gap-2 items-center">
              <Button size="sm" variant="outline" className="rounded-xl" onClick={onUpload} data-testid="profile-upload">
                <Sparkles className="w-4 h-4 mr-1" /> AI upload
              </Button>
              <Button size="sm" variant="outline" className="rounded-xl" onClick={onEdit} data-testid="profile-edit">
                <Pencil className="w-4 h-4 mr-1" /> Edit
              </Button>
              <Button size="sm" variant="outline" className="rounded-xl" onClick={downloadZip} data-testid="profile-zip">
                <FolderArchive className="w-4 h-4 mr-1" /> Records ZIP
              </Button>
              <div className="ml-auto flex items-center gap-2">
                <input
                  type="text" value={reviewer} onChange={(e) => setReviewer(e.target.value)}
                  placeholder="Reviewer name (optional)"
                  className="h-9 rounded-md border border-stone-200 px-3 text-sm w-56"
                  data-testid="profile-reviewer"
                />
                <Button size="sm" variant="outline" className="rounded-xl border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                  onClick={() => printChecklist(false)} data-testid="profile-print">
                  <Printer className="w-4 h-4 mr-1" /> Print
                </Button>
                <Button size="sm" className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white"
                  onClick={() => printChecklist(true)} data-testid="profile-print-full">
                  <Printer className="w-4 h-4 mr-1" /> Print + certifications
                </Button>
              </div>
            </div>

            {/* PD Hours Ledger — professional read-out of annual PD progress */}
            <PDHoursLedger checklist={checklist} />

            {/* Checklist grouped by section */}
            <div className="mt-4 space-y-4" data-testid="profile-checklist">
              {sections.order.map(sec => (
                <div key={sec} className="rounded-xl border border-stone-200 bg-white overflow-hidden">
                  <div className="px-4 py-2 bg-stone-50 border-b border-stone-200">
                    <div className="text-[11px] uppercase tracking-widest font-semibold text-emerald-700">{sec}</div>
                  </div>
                  <div className="divide-y divide-stone-100">
                    {sections.g[sec].map(row => (
                      <RequirementRow
                        key={row.key}
                        row={row}
                        onDownload={downloadDoc}
                        onDelete={deleteDoc}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Unassigned documents */}
            {unassigned.length > 0 && (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/40" data-testid="profile-unassigned">
                <div className="px-4 py-2 border-b border-amber-200">
                  <div className="text-[11px] uppercase tracking-widest font-semibold text-amber-800 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Unassigned documents ({unassigned.length})
                  </div>
                  <div className="text-[11px] text-stone-500 mt-0.5">These files aren't linked to a checklist requirement yet. Click download to review or delete to remove.</div>
                </div>
                <div className="divide-y divide-amber-100">
                  {unassigned.map(d => (
                    <div key={d.id} className="px-4 py-2 flex items-center gap-3 text-sm">
                      <FileText className="w-4 h-4 text-stone-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="truncate font-medium text-stone-900">{d.title || d.file_name}</div>
                        <div className="text-[11px] text-stone-500">
                          {(d.category || "other").replace(/_/g, " ")}
                          {d.completion_date && <> · Completed {fmtDate(d.completion_date)}</>}
                          {d.expires_on && <> · Expires {fmtDate(d.expires_on)}</>}
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => downloadDoc(d)} data-testid={`unassigned-download-${d.id}`}>
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-800" onClick={() => deleteDoc(d)} data-testid={`unassigned-delete-${d.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RollupTile({ code, label, value }) {
  const meta = STATUS_MAP[code] || STATUS_MAP.review;
  return (
    <div className={`rounded-xl border p-3 ${meta.cls}`}>
      <div className="text-[10px] uppercase tracking-widest font-semibold opacity-70">{label}</div>
      <div className="text-lg font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function RequirementRow({ row, onDownload, onDelete }) {
  const state = row.state || {};
  const st = row.status || {};
  return (
    <div className="px-4 py-3" data-testid={`req-row-${row.key}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-medium text-stone-900">{row.label}</div>
            {row.cadence_label && (
              <span className="text-[10px] uppercase tracking-widest text-stone-500">{row.cadence_label}</span>
            )}
            <StatusChip code={st.code} label={st.label} />
          </div>
          <div className="text-[11px] text-stone-500 mt-1">
            {state.completed && <>Completed {fmtDate(state.completed)}</>}
            {state.expires && <>{state.completed ? " · " : ""}Expires {fmtDate(state.expires)}</>}
            {state.initials && <>{" · "}Init. <b>{state.initials}</b></>}
            {!state.completed && !state.expires && st.code !== "na" && <>No data yet — upload a document to auto-fill.</>}
          </div>
        </div>
      </div>
      {(row.docs || []).length > 0 && (
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
          {(row.docs || []).map(d => (
            <div key={d.id} className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-xs flex items-center gap-2">
              <FileText className="w-4 h-4 text-stone-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="truncate font-medium text-stone-900">{d.title || d.file_name}</div>
                <div className="text-[10px] text-stone-500">
                  {d.completion_date && <>Comp {fmtDate(d.completion_date)}</>}
                  {d.expires_on && <>{d.completion_date ? " · " : ""}Exp {fmtDate(d.expires_on)}</>}
                  {d.review_needed && <>{" · "}<span className="text-amber-700 font-semibold">Review</span></>}
                </div>
              </div>
              <button type="button" onClick={() => onDownload(d)} className="text-stone-500 hover:text-emerald-700" title="Download" data-testid={`doc-download-${d.id}`}>
                <Download className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => onDelete(d)} className="text-stone-400 hover:text-red-600" title="Delete" data-testid={`doc-delete-${d.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { StaffFileProfile };
